import { GridApi, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { concat, of, BehaviorSubject, forkJoin, firstValueFrom, switchMap, map, catchError, Subscription } from 'rxjs';
import moment from 'moment';
import { Injector } from '@angular/core';
import { toString as luceneToString, LuceneExpression } from 'lucene';

import { DatacenterSelectionService } from '../../../services/datacenter-selection.service';
import { OrderBy, OrderByDirection } from '../../../utils/constant';
import { PaginatedResult } from '../../../models/paginated-result';
import { formatNumber, formatTextDimension, parseNumberFromText } from '../../../utils/number.format';
import { FilterDateFormatType, DATE_FORMAT_PATTERN_DD_MM_YYYY,
	WEEKYEAR_FORMAT_PATTERN } from '../../../models/filter-formats';
import { CommonGridOptions } from '../../grid-options';
import { ColDefWithFields, ColumnType } from '../../column-type';
import { QualityIssueService } from '../../../../claim/services/quality-issue.service';
import { QualityIssue, CLOSED_STATUS_LIST, DECISION_TR, ISSUE_TYPE_TR,
	CLAIM_TYPE_TR } from '../../../../claim/models/quality-issue';
import { TranslationService } from '../../../services/translation.service';
import { OrderService } from '../../../../order/services/order.service';
import { Order } from '../../../../order/models/order';
import { Strategy } from '../strategy';
import { FilterModel } from '../../../models/ag-grid';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';

const LIMIT = 50;

function chunkArray(array: string[]) {
	const j = array.length;
	const tempArray = [];
	for (let i = 0; i < j; i += LIMIT) {
		const myChunk = array.slice(i, i + LIMIT);
		tempArray.push(myChunk);
	}
	return tempArray;
}

type QualityIssueAndDisplayer = (QualityIssue & {
	sOrderRef: SORefDisplay;
});

class SORefDisplay {
	orderSearched: boolean;
	orderRef: string;

	constructor(issue?: QualityIssue, orderSearched = false) {
		this.orderSearched = orderSearched;
		this.orderRef = issue?.supplierOrderNumber && issue.supplierOrderItemNumber
			? `${ issue.supplierOrderNumber }/${ issue.supplierOrderItemNumber }`
			: '';
	}

	toString() {
		return this.orderRef;
	}
}
export class StrategyQuality implements Strategy<QualityIssueAndDisplayer> {
	qualityIssueService: QualityIssueService;
	selectionService: DatacenterSelectionService;
	translationService: TranslationService;
	orderService: OrderService;
	defaultOrderBy = new OrderBy<keyof QualityIssue>('creationDate', OrderByDirection.DESC);
	hasTotal = false;
	aggregateFields = [];
	qI?: PaginatedResult<QualityIssueAndDisplayer>;

	constructor(injector: Injector) {
		this.qualityIssueService = injector.get<QualityIssueService>(QualityIssueService);
		this.selectionService = injector.get<DatacenterSelectionService>(DatacenterSelectionService);
		this.translationService = injector.get<TranslationService>(TranslationService);
		this.orderService = injector.get<OrderService>(OrderService);
	}

	setTotal(_api?: GridApi, _filter?: string): Subscription {
		throw new Error('Method not implemented.');
	}

	async getAllDataSelected(
		currentColumns: string[],
		filter: string | undefined,
		orderBy: OrderBy<keyof QualityIssue>[],
	) {
		return this.getAllData(orderBy, currentColumns, filter)
			.then(qualityIssue => {
				if (!qualityIssue) {
					return [];
				}
				return this.removeDeselectedRows(qualityIssue);
			});
	}

	async getAllData(
		orderBy: OrderBy<keyof QualityIssue>[],
		currentColumns: string[],
		filter: string | undefined
	) {
		let currentColumnValues = currentColumns;
		if (currentColumnValues.includes('mill')) {
			currentColumnValues = currentColumnValues.filter(column => column !== 'mill');
			if (!currentColumnValues.includes('supplierOrderNumber')) {
				currentColumnValues.push('supplierOrderNumber');
			}
			if (!currentColumnValues.includes('supplierOrderItemNumber')) {
				currentColumnValues.push('supplierOrderItemNumber');
			}
		}
		return firstValueFrom(this.qualityIssueService.getIssues(
			<(keyof QualityIssue)[]>currentColumnValues,
			filter,
			0,
			60_000,
			orderBy,
			false
		).pipe(switchMap(qualityIssue => {
			this.qI = {
				...qualityIssue,
				results: qualityIssue.results.map(v => ({ ...v, sOrderRef: new SORefDisplay(v) }))
			};
			const notSearched = new Set<string>();
			for (const resu of qualityIssue.results.map(issue =>
				new SORefDisplay(issue)).filter(value => value.orderRef)) {
				notSearched.add(resu.orderRef);
			}
			const conditions = Array.from(notSearched);
			const newChunkedArray = chunkArray(conditions);
			const chunkedGetOrderCalls = newChunkedArray.map(chunkedConditions => {
				let allCondition: LuceneExpression;
				if (chunkedConditions.length === 1) {
					allCondition = {
						field: 'id',
						term: chunkedConditions[0],
						quoted: true
					};
				} else {
					allCondition = {
						field: 'id',
						left: {
							field: '<implicit>',
							term: chunkedConditions[0],
							quoted: true
						},
						operator: '<implicit>',
						right: {
							field: '<implicit>',
							term: chunkedConditions[1],
							quoted: true
						},
						parenthesized: true
					};
					let current = allCondition;
					for (const condition of chunkedConditions.slice(2)) {
						current.right = {
							left: current.right,
							operator: '<implicit>',
							right: {
								field: '<implicit>',
								term: condition,
								quoted: true
							},
							parenthesized: false
						};
						current = current.right;
					}
				}
				const orderByCON: OrderBy<keyof Order>[] = [];
				orderByCON.push(new OrderBy('customerOrderNumber', OrderByDirection.ASC));
				return this.orderService.getOrders(
					[
						'customerOrderNumber',
						'customerOrderItemNumber',
						'supplierOrderNumber',
						'supplierOrderItemNumber',
						'soldToDescription',
						'millDescription'
					],
					luceneToString(allCondition),
					0,
					60_000,
					orderByCON,
					false
				).pipe(
					map(result => result.results),
					catchError((err: unknown) => {
						console.error('Orders not found :', err);
						return of(undefined);
					})
				);
			});
			return forkJoin(chunkedGetOrderCalls);
		}))).then(res => {
			if (!this.qI) {
				return undefined;
			} else if (Array.isArray(res)) {
				const resOrders = res.flatMap(r => r ?? []);
				for (const issue of this.qI.results) {
					const orderFound = resOrders.find(
						order => order.supplierOrderNumber === issue.supplierOrderNumber
							&& order.supplierOrderItemNumber === String(issue.supplierOrderItemNumber)
					);
					issue.order = orderFound;
					issue.sOrderRef = new SORefDisplay(issue, true);
				}
			}
			return this.qI;
		}).catch((error: unknown) => {
			console.error('Error:', error);
		});
	}

	formatCellByType(columnDef: ColDefWithFields<QualityIssueAndDisplayer>, data: QualityIssue) {
		const field = columnDef.field || '';
		const fields = columnDef.fields || [];
		const { columnType } = columnDef;
		const columnId = columnDef.colId;
		switch (columnType) {
		case ColumnType.DIMENSION_COLUMN:
			return formatTextDimension(
					<string>data[<keyof QualityIssue>fields[0]],
					<string>data[<keyof QualityIssue>fields[1]],
					<string>data[<keyof QualityIssue>fields[2]]
			);
		case ColumnType.ORDER_NUMBER_COLUMN:
		{
			let orderNumber = '';
			if (<string> data[<keyof QualityIssue> fields[0]]) {
				orderNumber = <string> data[<keyof QualityIssue> fields[0]];
			}
			if (<string> data[<keyof QualityIssue> fields[0]] && <string> data[<keyof QualityIssue> fields[1]]) {
				orderNumber += `/${ <string> data[<keyof QualityIssue> fields[1]] }`;
			}
			return orderNumber;
		}
		case ColumnType.NUMBER_COLUMN:
		{
			const value = <number | undefined>data[<keyof QualityIssue>field];
			return value === undefined ? '' : String(value);
		}
		case ColumnType.TEXT_COLUMN:
			return this.formatingTextColumn(field, columnId, data);
		case ColumnType.DATE_COLUMN:
			if (data[<keyof QualityIssue>field]) {
				const dateformat = (<FilterDateFormatType>columnDef.cellRendererParams).outputFormat === 'week'
					? WEEKYEAR_FORMAT_PATTERN : DATE_FORMAT_PATTERN_DD_MM_YYYY;
				return moment(<string>data[<keyof QualityIssue>field]).format(dateformat);
			}
			return '';
		default:
			throw new Error('unknown filter type');
		}
	}

	formatingTextColumn(field: string, colId: string | undefined, data?: QualityIssue) {
		if (!colId || data === undefined) {
			return '';
		}
		if (colId === 'origin') {
			const issue = <QualityIssue | undefined>data;
			return issue?.origin
				? this.translationService.getIssueOriginTr(issue.origin)
				: '';
		}
		if (colId === 'mill') {
			const issue = <QualityIssue | undefined>data;
			return issue?.order
				? issue.order.millDescription
				: '';
		}
		if (colId === 'issueType') {
			const issue = <QualityIssue | undefined>data;
			return issue?.issueType
				? this.translationService.getTR(this.getIssueTypeTr(issue.issueType))
				: '';
		}
		if (colId === 'claimType') {
			const issue = <QualityIssue | undefined>data;
			return issue?.claimType
				? this.translationService.getTR(this.getClaimTypeTr(issue.claimType))
				: '';
		}
		if (colId === 'status') {
			const issue = <QualityIssue | undefined>data;
			if (issue?.status === undefined) {
				return '';
			}
			const threeYearsAgo = moment().subtract(3, 'year');
			const claimCloseDate = moment(issue.closedDate);
			const isArchived = threeYearsAgo.isAfter(claimCloseDate);
			return CLOSED_STATUS_LIST.includes(issue.status) && isArchived
				? this.translationService.getTR('claim.archived')
				: this.translationService.getTR(DECISION_TR[issue.status]);
		}
		if (colId === 'totalAmountAccepted') {
			const issue = <QualityIssue | undefined>data;
			if (!issue?.amountAccepted || !issue.currencyAccepted) {
				return '';
			}
			let acceptedAmt = parseNumberFromText(issue.amountAccepted);
			acceptedAmt = acceptedAmt !== undefined ? Math.abs(acceptedAmt) : acceptedAmt;
			return formatNumber(acceptedAmt, 'amount', issue.currencyAccepted);
		}
		if (colId === 'requestedTotalAmount') {
			const issue = <QualityIssue | undefined>data;
			return issue?.amountRequested && issue.currencyRequested
				? formatNumber(issue.amountRequested, 'amount', issue.currencyRequested)
				: '';
		}
		if (colId === 'claimQuantity') {
			const issue = <QualityIssue | undefined>data;
			if (!issue) {
				return '';
			}
			return issue.qtyRequested ? `${ issue.qtyRequested } t` : '';
		}
		if (colId === 'acceptedQuantity') {
			const issue = <QualityIssue | undefined>data;
			if (!issue) {
				return '';
			}
			return issue.qtyAccepted ? `${ issue.qtyAccepted } t` : '';
		}
		if (colId === 'batchReferenceNumber') {
			const issue = <QualityIssue | undefined>data;
			let batchsNumber = '';
			if (issue) {
				batchsNumber = issue.batches.map(item => item.batchReference).join(',');
			}
			return batchsNumber;
		}
		if (colId === 'customerOrderNumber') {
			const issue = <QualityIssue | undefined>data;
			return issue?.customerOrderNumber && issue.customerOrderItemNumber
				? `${ issue.customerOrderNumber }/${ issue.customerOrderItemNumber }`
				: '';
		}
		if (colId === 'customerDefect') {
			const issue = <QualityIssue | undefined>data;
			return issue?.customerDefectDetailed && issue.customerDefectDetailedDescription
				? issue.customerDefectDetailedDescription : '';
		}
		return <string>data[<keyof QualityIssue>field];
	}

	getIssueTypeTr(type: string) {
		if (type === 'warning') {
			return ISSUE_TYPE_TR.warning;
		}
		return ISSUE_TYPE_TR.claim;
	}

	getClaimTypeTr(type: string) {
		if (type === 'ZL') {
			return CLAIM_TYPE_TR.ZL;
		}
		return CLAIM_TYPE_TR.ZR;
	}

	getCustomGridOptions(defaultGridOptions: CommonGridOptions) {
		return new CommonGridOptions<QualityIssueAndDisplayer>({
			...defaultGridOptions,
			getRowId: ({ data }: { data: QualityIssue }) => data.id
		});
	}

	getDataFromGrid(
		orderBy: OrderBy<keyof QualityIssue>[],
		currentColumns: (keyof QualityIssue)[],
		filter: string | undefined,
		params: IServerSideGetRowsParams<QualityIssueAndDisplayer>,
		rowsCountSubject: BehaviorSubject<number | undefined>,
		gridApi: GridApi<QualityIssueAndDisplayer>,
	) {
		let currentColumnsWithId = this.addIdColumn(currentColumns);
		if (currentColumnsWithId.includes('mill')) {
			currentColumnsWithId = currentColumnsWithId.filter(column => column !== 'mill');
			if (!currentColumnsWithId.includes('supplierOrderNumber')) {
				currentColumnsWithId.push('supplierOrderNumber');
			}
			if (!currentColumnsWithId.includes('supplierOrderItemNumber')) {
				currentColumnsWithId.push('supplierOrderItemNumber');
			}
		}
		return this.qualityIssueService.getIssues(
			currentColumnsWithId,
			filter,
			params.request.startRow ?? 0,
			250,
			orderBy,
			false
		).pipe(
			switchMap(res => {
				const notSearched = new Set<string>();
				gridApi.forEachNode(node => {
					const qualityIssue = node.data;
					if (!qualityIssue) {
						return;
					}
					if (qualityIssue.sOrderRef.orderRef && !qualityIssue.sOrderRef.orderSearched) {
						notSearched.add(qualityIssue.sOrderRef.orderRef);
					}
				});
				if (res.count === 0) {
					return of(res);
				}
				for (const resu of res.results.map(issue => new SORefDisplay(issue)).filter(value => value.orderRef)) {
					notSearched.add(resu.orderRef);
				}
				const conditions = Array.from(notSearched);
				const newChunkedArray = chunkArray(conditions);
				const chunkedGetOrderCalls = newChunkedArray.map(chunkedConditions => {
					let allCondition: LuceneExpression;
					if (chunkedConditions.length === 1) {
						allCondition = {
							field: 'id',
							term: chunkedConditions[0],
							quoted: true
						};
					} else {
						allCondition = {
							field: 'id',
							left: {
								field: '<implicit>',
								term: chunkedConditions[0],
								quoted: true
							},
							operator: '<implicit>',
							right: {
								field: '<implicit>',
								term: chunkedConditions[1],
								quoted: true
							},
							parenthesized: true
						};
						let current = allCondition;
						for (const condition of chunkedConditions.slice(2)) {
							current.right = {
								left: current.right,
								operator: '<implicit>',
								right: {
									field: '<implicit>',
									term: condition,
									quoted: true
								},
								parenthesized: false
							};
							current = current.right;
						}
					}
					const orderByCON: OrderBy<keyof Order>[] = [];
					orderByCON.push(new OrderBy('customerOrderNumber', OrderByDirection.ASC));
					return this.orderService.getOrders(
						[
							'customerOrderNumber',
							'customerOrderItemNumber',
							'supplierOrderNumber',
							'supplierOrderItemNumber',
							'soldToDescription',
							'millDescription'
						],
						luceneToString(allCondition),
						0,
						LIMIT,
						orderByCON,
						false
					).pipe(
						map(result => result.results),
						catchError((err: unknown) => {
							console.error('Orders not found :', err);
							return of(undefined);
						})
					);
				});
				return concat(of(res), forkJoin(chunkedGetOrderCalls));
			})
		).subscribe({
			next: res => {
				if (Array.isArray(res)) {
					const resOrders = res.flatMap(r => r ?? []);
					gridApi.forEachNode(node => {
						const qualityIssue = node.data;
						if (!qualityIssue) {
							return;
						}
						const orderFound = resOrders.find(
							order => order.supplierOrderNumber === qualityIssue.supplierOrderNumber
								&& order.supplierOrderItemNumber === String(qualityIssue.supplierOrderItemNumber)
						);
						if (!orderFound) {
							qualityIssue.sOrderRef.orderSearched = true;
							return;
						}
						qualityIssue.order = orderFound;
						qualityIssue.sOrderRef = new SORefDisplay(qualityIssue, true);
						node.setData(qualityIssue);
					});
				} else {
					const lastRow = params.request.endRow === undefined || res.count <= params.request.endRow
						? res.count
						: -1;
					rowsCountSubject.next(res.count);
					params.success({
						rowData: res.results.map(v => ({ ...v, sOrderRef: new SORefDisplay(v) })),
						rowCount: lastRow,
					});
				}
			},
			error: (err: unknown) => {
				console.error('error requesting datacenter', err);
				rowsCountSubject.next(undefined);
				params.fail();
			}
		});
	}

	addIdColumn(currentColumns: (keyof QualityIssue)[]) {
		if (!currentColumns.includes('id')) {
			currentColumns.push('id');
		}
		return currentColumns;
	}

	createAggregateCustomViewFilter(_view: UserDatacenterView): string {
		throw new Error('Method not implemented.');
	}

	createAggregateQuickSearchFilter(
		_currentSelectedView: UserDatacenterView,
		_api?: GridApi,
		_filterModel?: FilterModel
	): string {
		throw new Error('Method not implemented.');
	}

	private removeDeselectedRows(qualityIssues: PaginatedResult<QualityIssueAndDisplayer>) {
		return qualityIssues.results.filter(qualityIssue => {
			const rowDeselected = this.selectionService.findDeselectedNode(qualityIssue.id);
			return !rowDeselected;
		});
	}
}
